'use client';

import { useDebouncedEffect } from '@react-hookz/web';
import { cn, Input, Textarea } from '@scatterlab/zeta-core';
import {
  ComponentPropsWithRef,
  CSSProperties,
  memo,
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { mergeRefs } from 'react-merge-refs';

const copyStyles = (styles: CSSStyleDeclaration, node: HTMLElement) => {
  /* eslint-disable no-param-reassign */
  node.style.fontSize = styles.fontSize;
  node.style.fontFamily = styles.fontFamily;
  node.style.fontWeight = styles.fontWeight;
  node.style.fontStyle = styles.fontStyle;
  node.style.lineHeight = styles.lineHeight;
  node.style.letterSpacing = styles.letterSpacing;
  node.style.textTransform = styles.textTransform;
  node.style.paddingLeft = styles.paddingLeft;
  node.style.paddingRight = styles.paddingRight;
  node.style.paddingTop = styles.paddingTop;
  node.style.paddingBottom = styles.paddingBottom;
};

export type FlexibleTextareaProps<
  Component extends typeof Input | typeof Textarea = typeof Textarea,
> = ComponentPropsWithRef<Component> & {
  component?: Component;
  containerClassName?: string;
  contentContainerClassName?: string;
  className?: string;
  children?: ReactNode;
  containerStyle?: CSSProperties;
};

const FlexibleTextarea = <
  Component extends typeof Input | typeof Textarea = typeof Textarea,
>({
  className,
  containerClassName,
  contentContainerClassName,
  component,
  children,
  placeholder,
  value,
  containerStyle,
  ref,
  ...props
}: FlexibleTextareaProps<Component>) => {
  const internalRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const sizerRef = useRef<HTMLDivElement>(null);
  const placeHolderSizerRef = useRef<HTMLDivElement>(null);

  const updateInputSize = useCallback(() => {
    if (!internalRef.current) return;

    let newInputWidth;
    let newInputHeight;
    if (internalRef.current.placeholder && !internalRef.current.value) {
      newInputWidth =
        Math.max(
          sizerRef.current?.scrollWidth || 0,
          placeHolderSizerRef.current?.scrollWidth || 0,
        ) + 14;
      newInputHeight = Math.max(
        sizerRef.current?.scrollHeight || 0,
        placeHolderSizerRef.current?.scrollHeight || 0,
      );
    } else {
      newInputWidth = (sizerRef.current?.scrollWidth || 0) + 14;
      newInputHeight = sizerRef.current?.scrollHeight || 0;
    }

    internalRef.current.style.width = `${newInputWidth}px`;
    internalRef.current.style.minHeight = `${newInputHeight}px`;
  }, []);

  useEffect(() => {
    if (!window.getComputedStyle) {
      return;
    }
    const inputStyles =
      internalRef.current && window.getComputedStyle(internalRef.current);
    if (!inputStyles) {
      return;
    }
    if (sizerRef.current) {
      copyStyles(inputStyles, sizerRef.current);
    }
    if (placeHolderSizerRef.current) {
      copyStyles(inputStyles, placeHolderSizerRef.current);
    }
  }, []);

  useDebouncedEffect(
    () => {
      const observer = new ResizeObserver(updateInputSize);

      observer.observe(sizerRef.current!);

      return () => observer.disconnect();
    },
    [updateInputSize],
    100,
  );

  useEffect(updateInputSize);

  const TextareaComponent = component ?? Textarea;

  return (
    <div
      className={cn('relative max-w-full', containerClassName)}
      style={containerStyle}
    >
      <div
        className={cn(
          'flex max-w-full flex-row items-start justify-start',
          contentContainerClassName,
        )}
      >
        <TextareaComponent
          value={value}
          placeholder={placeholder}
          className={cn(
            'box-border flex resize-none flex-col self-start overflow-hidden break-all px-3 py-2 outline-none transition-none placeholder:text-white/50',
            className,
          )}
          rows={1}
          {...props}
          ref={mergeRefs([ref, internalRef]) as Ref<never>}
        />
        {children}
      </div>
      <div
        className={cn(
          'invisible absolute inset-x-0 top-0 z-[-10] flex max-w-full flex-row items-start justify-start',
          contentContainerClassName,
        )}
      >
        <div className="flex flex-1 flex-row">
          <div
            ref={sizerRef}
            className="box-border overflow-scroll whitespace-pre-wrap break-all"
          >
            {`${value}|`}
          </div>
          {placeholder && (
            <div
              ref={placeHolderSizerRef}
              className="box-border overflow-scroll whitespace-pre-wrap break-all"
            >
              {`${placeholder}|`}
            </div>
          )}
        </div>
        {children}
      </div>
    </div>
  );
};

export default memo(FlexibleTextarea);
